/**
 * The shared vocabulary of a tabular ingest contract — the rules a config's
 * output tables, columns and inputs must satisfy before they reach DuckDB.
 *
 * This lives in `ontology-core` because it is the one package both the
 * browser (config editor, upload wizard) and the service (proposal parser,
 * output checks, routes) value-import. `@atbash-labs/ontology-ingest`
 * re-exports everything here so service code needs no import changes. The
 * proxy keeps its own Go copy of each list; `schema-drift.spec.ts` in the
 * ingest package proves the two never disagree.
 */

import { DB_REGISTRY_CONNECTION_NAME_RE, isDbRegistryConnectionName } from '../utils/urn'

/**
 * The DuckDB column types a dump contract may declare — the TypeScript twin
 * of the proxy's `allowedDumpColumnTypes` (dbconn/dump_schema.go). The
 * mapping-config JSON schema and the builder's tool schema enumerate the
 * same list.
 */
export const TABULAR_DUMP_COLUMN_TYPES = [
  'BOOLEAN',
  'BIGINT',
  'DOUBLE',
  'VARCHAR',
  'DATE',
  'TIME',
  'TIMESTAMP'
] as const

export type TabularDumpColumnType = (typeof TABULAR_DUMP_COLUMN_TYPES)[number]

export function isTabularDumpColumnType(value: unknown): value is TabularDumpColumnType {
  return (
    typeof value === 'string' && (TABULAR_DUMP_COLUMN_TYPES as readonly string[]).includes(value)
  )
}

/**
 * Table and column identifiers: the proxy's `dumpTableNameRe` plus its
 * 128-character cap. Both halves are checked wherever a name is accepted so
 * a contract that passes here cannot be refused later by DuckDB's DDL.
 */
export const TABULAR_IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/
export const TABULAR_IDENTIFIER_MAX = 128

export function isTabularIdentifier(value: string): boolean {
  return value.length <= TABULAR_IDENTIFIER_MAX && TABULAR_IDENTIFIER_RE.test(value)
}

/**
 * DuckDB folds unquoted identifiers, so `Sku` and `sku` name one column and
 * `Orders` and `orders` one table. Every uniqueness or membership check on
 * identifiers compares this form, matching what the proxy does in
 * `dbconn/dump_schema.go`.
 */
export function foldTabularIdentifier(value: string): string {
  return value.toLowerCase()
}

/** What `tabularContractProblems` reads of an output table. */
export interface TabularContractTableShape {
  table: string
  mode: string
  key: readonly string[]
  ingestOncePerAsOfDate?: boolean
  columns: readonly { name: string }[]
}

/**
 * One way a contract's tables fail the shape rules, located by index so a
 * caller can point at the offending row. The wording is the caller's: the
 * service prefixes a JSON path, the editor writes a sentence.
 */
export type TabularContractProblem =
  | { kind: 'no_tables' }
  | { kind: 'table_name_invalid'; tableIndex: number; table: string }
  | { kind: 'table_name_duplicate'; tableIndex: number; table: string }
  | { kind: 'no_columns'; tableIndex: number; table: string }
  | {
      kind: 'column_name_invalid' | 'column_name_duplicate'
      tableIndex: number
      table: string
      columnIndex: number
      column: string
    }
  | {
      kind: 'key_required'
      tableIndex: number
      table: string
      because: 'upsert' | 'ingestOncePerAsOfDate'
    }
  | {
      kind: 'key_column_duplicate' | 'key_not_a_column'
      tableIndex: number
      table: string
      keyIndex: number
      column: string
    }

/**
 * The shape rules every output contract must satisfy, checked once here for
 * the proposal parser and the contract editor alike: at least one table with
 * at least one column, table and column names that are identifiers and
 * unique once folded, a natural key for `upsert` and for
 * `ingestOncePerAsOfDate`, and key columns that are distinct and name a
 * column of their table. Returns every problem, in table order, so an editor
 * can list them all; the service throws the first. Types, sizes and field
 * shapes are the parser's business and are not checked here.
 */
export function tabularContractProblems(
  tables: readonly TabularContractTableShape[]
): TabularContractProblem[] {
  const problems: TabularContractProblem[] = []
  if (!tables.length) problems.push({ kind: 'no_tables' })
  const tableNames = new Set<string>()
  tables.forEach((table, tableIndex) => {
    const name = table.table
    const at = { tableIndex, table: name }
    if (!isTabularIdentifier(name)) problems.push({ kind: 'table_name_invalid', ...at })
    else if (tableNames.has(foldTabularIdentifier(name))) {
      problems.push({ kind: 'table_name_duplicate', ...at })
    }
    tableNames.add(foldTabularIdentifier(name))
    if (!table.columns.length) problems.push({ kind: 'no_columns', ...at })
    const columnNames = new Set<string>()
    table.columns.forEach((column, columnIndex) => {
      const folded = foldTabularIdentifier(column.name)
      const where = { ...at, columnIndex, column: column.name }
      if (!isTabularIdentifier(column.name)) {
        problems.push({ kind: 'column_name_invalid', ...where })
      } else if (columnNames.has(folded)) {
        problems.push({ kind: 'column_name_duplicate', ...where })
      }
      columnNames.add(folded)
    })
    if ((table.mode === 'upsert' || table.ingestOncePerAsOfDate) && !table.key.length) {
      problems.push({
        kind: 'key_required',
        ...at,
        because: table.mode === 'upsert' ? 'upsert' : 'ingestOncePerAsOfDate'
      })
    }
    const keyNames = new Set<string>()
    table.key.forEach((column, keyIndex) => {
      const folded = foldTabularIdentifier(column)
      const where = { ...at, keyIndex, column }
      if (keyNames.has(folded)) problems.push({ kind: 'key_column_duplicate', ...where })
      else if (!columnNames.has(folded)) problems.push({ kind: 'key_not_a_column', ...where })
      keyNames.add(folded)
    })
  })
  return problems
}

/**
 * The file types a config build accepts as examples. A run of the finished
 * config accepts what its `input.accept` says, which is drawn from this list,
 * so anything the wizard offers for either path must appear here.
 */
export const TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS = [
  '.csv',
  '.xlsx',
  '.xlsm',
  '.xls',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.tif',
  '.tiff'
] as const

export type TabularConfigBuildAcceptedExtension =
  (typeof TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS)[number]

export function isTabularConfigBuildAcceptedExtension(
  value: string
): value is TabularConfigBuildAcceptedExtension {
  return (TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS as readonly string[]).includes(value)
}

/** The extension of a file name, lower-cased with its dot, or '' when there is none. */
export function tabularFileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot).toLowerCase()
}

/**
 * A mapped tabular config's name is also its DuckDB connection name
 * (`urn:db:<name>`), so there is exactly one rule for it: the registry's.
 * Every surface that accepts a new name — wizard, begin route, saved
 * definition — checks this and shows `TABULAR_CONFIG_NAME_HELP`.
 */
export const TABULAR_CONFIG_NAME_RE = DB_REGISTRY_CONNECTION_NAME_RE
export const TABULAR_CONFIG_NAME_HELP =
  "Letters, digits, '.', '_' or '-', starting with a letter or digit (up to 128 characters)."

export function isTabularConfigName(value: string): boolean {
  return isDbRegistryConnectionName(value)
}

/**
 * How many input files one run may carry. The stored parser reads every file
 * in the sandbox's document directory in one invocation — the way the builder
 * developed and replayed it over all the examples together — so an upload of
 * many files is one sandbox, one script run and one write per table, not a
 * VM per file.
 */
export const MAX_TABULAR_INPUT_FILES_PER_RUN = 100

/**
 * Columns an agent run fills from the page itself, never from the model:
 * where each row came from, and whether a person should check it.
 */
export const TABULAR_SERVICE_FILLED_COLUMNS: readonly string[] = [
  'source_page_urn',
  'source_file',
  'source_page',
  'needs_review'
]
